/** @prettier */

import React from 'react';
import {
  Grid,
  List,
  Card,
  CardHeader,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemButton,
  Checkbox,
  Button,
  Divider,
  Stack,
} from '@mui/material';
import {regunitType} from '../Type/TypeDef';

//interface import
import * as apiIF from '../Type/api';
//Style import
import '../css/common/common.css';

import {useTranslation} from 'react-i18next';

function not(a: regunitType[], b: regunitType[]) {
  return a.filter(value => b.indexOf(value) === -1);
}

function intersection(a: regunitType[], b: regunitType[]) {
  return a.filter(value => b.indexOf(value) !== -1);
}

function union(a: regunitType[], b: regunitType[]) {
  return [...a, ...not(b, a)];
}

/**
 * グループユニット選択
 * @param props props
 * @returns JSX
 * - left グループ未登録室内機
 * - right グループ登録室内機
 * - setLeft グループ未登録室内機
 * - setRight グループ登録室内機
 * - slInfo 空調機情報(省略可)
 * - slOtherInfo IF情報(省略可)
 */
const TransferList = (props: {
  left: regunitType[];
  right: regunitType[];
  setLeft: React.Dispatch<React.SetStateAction<regunitType[]>>;
  setRight: React.Dispatch<React.SetStateAction<regunitType[]>>;
  slInfo?: apiIF.res.getCompositionsSLUnit[];
  slOtherInfo?: apiIF.res.getCompositionsSLIFUnit[];
}) => {
  const [checked, setChecked] = React.useState<regunitType[]>([]);
  const leftChecked = intersection(checked, props.left);
  const rightChecked = intersection(checked, props.right);
  const trans = useTranslation().t; //翻訳関数

  /**
   * アイテム選択時の処理
   * @param value 室内機
   * @returns
   */
  const handleToggle = (value: regunitType) => () => {
    const currentIndex = checked.indexOf(value);
    const newChecked = [...checked];

    if (currentIndex === -1) {
      newChecked.push(value);
    } else {
      newChecked.splice(currentIndex, 1);
    }
    setChecked(newChecked);
  };

  /**
   * チェックされた項目の内、一覧に含まれる個数を算出
   * @param items 室内機一覧
   * @returns チェックされた項目の内、一覧に含まれる個数
   */
  const stringOfChecked = (items: regunitType[]) => {
    return intersection(checked, items).length;
  };

  /**
   * 全選択ボタン押下時処理
   * @param items
   * @returns
   */
  const handleToggleAll = (items: regunitType[]) => () => {
    if (stringOfChecked(items) === items.length) {
      setChecked(not(checked, items));
    } else {
      setChecked(union(checked, items));
    }
  };

  /**
   * 室内機グループ登録
   */
  const handleCheckedRight = () => {
    props.setRight(props.right.concat(leftChecked));
    props.setLeft(not(props.left, leftChecked));
    setChecked(not(checked, leftChecked));
  };

  /**
   * 室内機グループ解除
   */
  const handleCheckedLeft = () => {
    props.setLeft(props.left.concat(rightChecked));
    props.setRight(not(props.right, rightChecked));
    setChecked(not(checked, rightChecked));
  };

  /**
   * 即時関数でTransferList生成時にのみ１回だけ、ユニットIDとSLアドレスの配列を生成する
   * iuIdList は unitIdがキーで unitId に対応したSLアドレスが格納されている
   */
  const iuIdList = (() => {
    let iuIdObj: {[key: string]: number} = {};

    if (props.slInfo) {
      for (let slInfo of props.slInfo) {
        for (let ouInfo of slInfo['outdoor-unit-informations']) {
          if (ouInfo['indoor-unit-informations'] === undefined) continue;
          for (let iuInfo of ouInfo['indoor-unit-informations']) {
            iuIdObj[iuInfo['unit-id']] = Number(iuInfo['sl-address']);
          }
        }
      }
    }
    if (props.slOtherInfo) {
      for (let slOtherInfo of props.slOtherInfo) {
        for (let unitInfo of slOtherInfo['unit-informations']) {
          iuIdObj[unitInfo['unit-id']] = Number(unitInfo['sl-address']);
        }
      }
    }
    return iuIdObj;
  })();

  /**
   * SLアドレス取得関数
   * @param unitId 室内機ID
   * @returns SLアドレス
   */
  const getSLaddr = (unitId: string) => {
    // iuIdList は unitIdをキーとしたSLアドレスが格納されている
    return iuIdList[unitId];
  };

  /**
   * SLアドレスでソートする
   * @param items 室内機一覧
   * @returns 室内機一覧
   */
  const iuSort = (items: regunitType[]): regunitType[] => {
    const sortData = items
      //ソート用にSLアドレス付のデータを作成
      .map(value => ({
        slAddr: Number(getSLaddr(value['unit-id'])),
        data: value,
      }))
      //SLアドレスでソート
      .sort((a, b) => a.slAddr - b.slAddr);

    //ソート済データからリスト用データのみ出力する
    return sortData.map(value => value.data);
  };

  /**
   * 室内機一覧表示
   * @param title タイトル
   * @param items アイテム(室内機)
   * @param id ID(省略可)
   * @returns JSX
   */
  const CustomList = (
    title: React.ReactNode,
    items: regunitType[],
    id?: string
  ) => {
    return (
      <Card>
        <CardHeader
          avatar={
            <Checkbox
              onClick={handleToggleAll(items)}
              checked={
                stringOfChecked(items) === items.length && items.length !== 0
              }
              indeterminate={
                stringOfChecked(items) !== items.length &&
                stringOfChecked(items) !== 0
              }
              disabled={items.length === 0}
              inputProps={{'aria-label': 'all items selected'}}
              id="mv-transferUnregisterGpAllSelect"
            />
          }
          title={title}
          subheader={`${stringOfChecked(items)}/${items.length} ${trans(
            'TransferList.subheaderTxt'
          )}`}
        />
        <Divider />
        <List
          dense
          component="div"
          role="list"
          className="groupSetting__contents__registrationIU__iuList"
          id={`${id}-unitList`}>
          {/* SLアドレス順にソートして出力する */}
          {iuSort(items).map(value => {
            const labelId = `transfer-list-all-item-${value}-label`;
            return (
              <ListItem key={value['unit-id']} role="listitem" disablePadding>
                <ListItemButton
                  onClick={handleToggle(value)}
                  id={`${value['unit-name']}chkBox`}>
                  <ListItemIcon>
                    <Checkbox
                      checked={checked.indexOf(value) !== -1}
                      tabIndex={-1}
                      disableRipple
                      inputProps={{'aria-labelledby': labelId}}
                      id={`mv-transferGpSelect${value['unit-name']}`}
                    />
                  </ListItemIcon>
                  <ListItemText
                    id={labelId}
                    primary={getSLaddr(value['unit-id'])}
                    primaryTypographyProps={{
                      color: 'textSecondary',
                      style: {
                        display: 'inline-block',
                        width: 24 + 'px',
                        textAlign: 'right',
                      },
                    }}
                    secondary={value['unit-name']}
                    secondaryTypographyProps={{
                      component: 'span',
                      color: 'textPrimary',
                      style: {
                        display: 'inline-block',
                        marginLeft: 15 + 'px',
                      },
                    }}
                  />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      </Card>
    );
  };

  return (
    <Grid
      container
      spacing={2}
      justifyContent="center"
      alignItems="center"
      className="groupSetting__contents__registrationIU">
      <Grid item xs={5} md={4}>
        {CustomList(
          trans('TransferList.title.unselected'),
          props.left,
          'mv-chkUnselectedAll'
        )}
      </Grid>
      <Grid item xs={2} md={1}>
        <Stack spacing={1}>
          <Button
            variant="outlined"
            size="small"
            onClick={handleCheckedRight}
            disabled={leftChecked.length === 0}
            aria-label="move selected right"
            id="mv-btnMoveRgister">
            &gt;
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={handleCheckedLeft}
            disabled={rightChecked.length === 0}
            aria-label="move selected left"
            id="mv-btnMoveUnrgister">
            &lt;
          </Button>
        </Stack>
      </Grid>
      <Grid item xs={5} md={4}>
        {CustomList(
          trans('TransferList.title.selected'),
          props.right,
          'mv-chkSelectedAll'
        )}
      </Grid>
    </Grid>
  );
};
export default TransferList;
